import { useState } from 'react'

import { t } from '@ctcells/i18n'

import { TextInput, type TextInputProps } from './TextInput.tsx'

import './form.css'

/**
 * 비밀번호 칸 오른쪽 「눈」 단추 — eric 2026-10-02 「비밀번호 적을 때.. 눈 표시 있어서.. 누르면..
 * 비밀번호가 보이게 하는 것도 적용해줘」.
 *
 * 🔴 **비밀번호 칸은 전부 이 단추를 같이 받는다** — 로그인 · 사용자 추가 · 비밀번호 변경 · 재설정.
 *    화면마다 따로 만들지 마라. 칸을 그리는 쪽이 `PasswordInput`(아래)을 쓰거나, 로그인처럼 칸의 틀을
 *    직접 짓는 곳은 이 단추만 가져다 쓴다.
 * 🔴 `type="button"` — 폼 안이라 기본값(submit)이면 누르는 순간 폼이 나간다(로그인이면 실패 횟수가 쌓인다).
 * 🔴 이름은 「비밀번호 표시」 하나로 고정이고 켜짐은 `aria-pressed` 가 말한다. 눈에는 **아이콘 모양**이
 *    말한다(눈 ↔ 사선 그은 눈) — 색만으로 상태를 말하지 않는다.
 * ⚠ 탭 순서는 DOM 순서다 — 칸 바로 뒤에 둔다.
 */
export type PasswordRevealButtonProps = {
  revealed: boolean
  onToggle: () => void
  /** 가리키는 입력 id(`aria-controls`) */
  controls?: string | undefined
  className?: string | undefined
}

export function PasswordRevealButton({
  revealed,
  onToggle,
  controls,
  className,
}: PasswordRevealButtonProps) {
  const label = t('auth.passwordReveal')
  return (
    <button
      aria-controls={controls}
      aria-label={label}
      aria-pressed={revealed}
      className={className === undefined ? 'a-pwreveal' : `a-pwreveal ${className}`}
      onClick={onToggle}
      title={label}
      type="button"
    >
      <RevealGlyph revealed={revealed} />
    </button>
  )
}

/**
 * 16 격자 · 획 1.6 · 둥근 끝 — 헤더의 시안 선 아이콘(`shell/navIcons.tsx`, eric 12b85e8)과 같은 규격.
 * 시안 집합에 눈이 없어 같은 규격으로 그렸다. 크기는 CSS(`--icon-lg`)가 정한다.
 * - 가림(기본): 사선 그은 눈 — 현재 가려진 상태
 * - 보임: 눈 — 현재 보이는 상태
 */
function RevealGlyph({ revealed }: { revealed: boolean }) {
  return (
    <svg
      aria-hidden="true"
      className="a-pwreveal__glyph"
      data-glyph={revealed ? 'eye' : 'eye-off'}
      fill="none"
      focusable="false"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.6}
      viewBox="0 0 16 16"
    >
      <path d="M1.5 8S3.9 3.5 8 3.5 14.5 8 14.5 8 12.1 12.5 8 12.5 1.5 8 1.5 8Z" />
      <circle cx="8" cy="8" r="2" />
      {revealed ? null : <path d="M2.5 2.5l11 11" />}
    </svg>
  )
}

/* ⚠ `TextInputProps` 는 비활성 축이 합집합이라 그냥 `Omit` 하면 짝(disabled ↔ 사유)이 풀린다 — 갈래마다 뺀다 */
type WithoutType<P> = P extends unknown ? Omit<P, 'type'> : never

/**
 * 비밀번호 입력 = `TextInput` + 눈 단추. 보임/가림은 **이 칸만의 화면 상태**다(보낼 값과 무관).
 * 🔴 단추가 서도 칸 폭이 안 변하게 오른쪽 여백을 늘 비워 둔다(`.a-pwd > .a-input`).
 * ⚠ 감싼 클래스 `a-pwd` 는 「추가」 창 규격(`addForm.css` — 값 칸 폭을 다 쓴다)이 이미 기다리던 이름이다.
 */
export type PasswordInputProps = WithoutType<TextInputProps> & {
  /** 감싼 상자에 더할 클래스 */
  boxClassName?: string | undefined
}

export function PasswordInput({ boxClassName, ...inputProps }: PasswordInputProps) {
  const [revealed, setRevealed] = useState(false)
  /*
   * 🔴 **빈 칸이면 눈 단추가 없다**(eric 2026-10-02 「텍스트가 아무것도 없으면 눈 모양 아이콘 안 보이게 해줘」).
   *    제어 칸은 `value` 로 바로 판정하고, 비제어 칸(`defaultValue`)은 DOM 의 `input` 을 듣는다.
   *    렌더하지 않으므로 탭 순서 · 접근성 트리에서도 빠진다.
   */
  const [domFilled, setDomFilled] = useState(() => (inputProps.defaultValue ?? '') !== '')
  const filled = inputProps.value !== undefined ? inputProps.value !== '' : domFilled
  /*
   * 🔴 비면 보임도 가림으로 되돌린다 — 안 되돌리면 빈 칸이 `type="text"` 로 남아 다음에 치는 값이 그대로 보인다.
   *    렌더 중 제 상태를 고치는 React 문서의 「이전 값에 맞춰 상태 조정」 꼴이다(effect 로 한 박자 늦게 고치지 않는다).
   */
  if (!filled && revealed) setRevealed(false)
  const shown = filled && revealed

  return (
    <span
      className={boxClassName === undefined ? 'a-pwd' : `a-pwd ${boxClassName}`}
      onInput={(event) => {
        if (event.target instanceof HTMLInputElement) setDomFilled(event.target.value !== '')
      }}
    >
      <TextInput {...inputProps} type={shown ? 'text' : 'password'} />
      {!filled ? null : (
        <PasswordRevealButton
          controls={inputProps.id}
          onToggle={() => setRevealed((value) => !value)}
          revealed={shown}
        />
      )}
    </span>
  )
}
